import React, { ChangeEvent, Ref } from 'react';
import { Divider, Row, Tooltip } from 'antd';
import { FormikContextType } from 'formik';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { path } from 'ramda';
import moment from 'moment';
import styled from 'styled-components';

import { SendCommunicationChannels, SendCommunicationCustomersSelection, SendCommunicationSecondStepForm } from 'types';
import FormError from 'components/molecules/form-error';
import { useIntl } from 'common/config/i18n';
import Input from 'components/organisms/input';

import InputSelect from '../select';

type ChannelsArePresentType = { mail?: boolean; sms?: boolean; whatsApp?: boolean; flashInfo: boolean };

export type Props = {
  elemRef?: Ref<HTMLDivElement>;
  formik: FormikContextType<SendCommunicationSecondStepForm>;
  customersSelection: SendCommunicationCustomersSelection;
  handleChannelChange: (e: CheckboxChangeEvent, id: SendCommunicationChannels) => void;
  channelsArePresent: ChannelsArePresentType;
  hasTargetChannels?: boolean;
  maxDaysBeforeDeferredSending?: number;
  canWriteDefferedCommunications: boolean;
  targetChannelsHeight?: number;
};

export const listOfHours = [
  '00',
  '01',
  '02',
  '03',
  '04',
  '05',
  '06',
  '07',
  '08',
  '09',
  '10',
  '11',
  '12',
  '13',
  '14',
  '15',
  '16',
  '17',
  '18',
  '19',
  '20',
  '21',
  '22',
  '23',
];

export const listOfMinutes = ['00', '10', '20', '30', '40', '50'];

export const SubTitle = styled.div`
  font-size: ${path(['theme', 'font', 'size', 'body'])};
  font-weight: ${path(['theme', 'font', 'weight', 'medium'])};
`;

export const StyledDivider = styled(Divider)<{ height: number }>`
  border-width: 2px;
  border-left-color: ${({ theme }) => theme.colors.grey.light};
  margin-right: 15px;
  margin-left: 10px;
  height: ${({ height }) => height}px;
  margin-bottom: -20px;
`;

export const StyledText = styled.div<{
  disabled: boolean;
}>`
  color: ${path(['theme', 'colors', 'grey', 'dark'])};
  color: ${({ disabled, theme }) => (disabled ? theme.colors.grey.dark : theme.colors.common.black)};
`;

export const getMinTimeForCommunication = () => {
  const currentTime = moment().add(15, 'minutes');
  const minutes = Math.ceil(currentTime.minutes() / 10) * 10;
  return currentTime.minutes(minutes).format('HH:mm');
};

export const isTimeBefore = (time1: string, time2: string): boolean => {
  const [hours1, minutes1] = time1.split(':').map(Number);
  const [hours2, minutes2] = time2.split(':').map(Number);

  if (hours1 < hours2) return true;
  if (hours1 > hours2) return false;
  return minutes1 < minutes2;
};

export const ErrorMessage = ({ formik }: { formik: Props['formik'] }) => {
  if (formik?.errors?.differedDaysBeforeCirculation) {
    return <FormError formik={formik} fieldName="differedDaysBeforeCirculation" />;
  }
  if (formik?.errors?.differedCommunicationTime) {
    return <FormError formik={formik} fieldName="differedCommunicationTime" />;
  }
  return null;
};

export const NumberDaysInput = ({
  name,
  min,
  max,
  value,
  disabled,
  labelDisabled,
  onChange,
}: {
  name: string;
  min: number;
  max: number;
  value?: number;
  disabled?: boolean;
  labelDisabled?: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}) => {
  const { t } = useIntl();

  return (
    <Row style={{ justifyContent: 'space-between' }}>
      <StyledText disabled={!!labelDisabled}>
        {t('sendCommunication', 'differedCommunication', 'differedNumberDaysLabel')}
      </StyledText>
      <Tooltip title={t('sendCommunication', 'differedCommunication', 'differedNumberDaysTooltip')} placement="top">
        <Input
          type="number"
          name={name}
          min={min}
          max={max}
          value={value}
          disabled={!!disabled}
          style={{ marginLeft: '10px' }}
          onChange={onChange}
        />
      </Tooltip>
    </Row>
  );
};

export const TimeSelect = ({
  minTime,
  value,
  disabled,
  labelDisabled,
  onChange,
}: {
  minTime: string;
  value?: string;
  disabled?: boolean;
  labelDisabled?: boolean;
  onChange: (time: string) => void;
}) => {
  const { t } = useIntl();

  const filterOption = (input: string, option?: { value: string }) => {
    if (!option) return false;

    const searchValue = input.replace(/:/g, '');
    const optionValue = option.value.replace(/:/g, '');

    return optionValue.startsWith(searchValue);
  };

  const inputSelectTimesData = [
    {
      id: 'validTimes',
      list:
        listOfHours
          .flatMap((hour) => listOfMinutes.map((minute) => `${hour}:${minute}`))
          .filter((time) => !isTimeBefore(time, minTime))
          .map((time) => ({ id: time, name: time })) || [],
    },
  ];

  return (
    <Row style={{ justifyContent: 'space-between', marginTop: '5px' }}>
      <StyledText disabled={!!labelDisabled}>
        {t('sendCommunication', 'differedCommunication', 'differedHourLabel')}
      </StyledText>
      <Tooltip
        title={t('sendCommunication', 'differedCommunication', 'differedHourTooltip')}
        placement="bottom"
        overlayStyle={{ zIndex: 1000 }}
      >
        <div>
          <InputSelect
            showSearch
            /**
             * hasMonospaceFont is used for smooth appearances: all the 'xy:zw' times have the same width
             * Try to remove it and check for examples '23:30' vs '11:20'
             */
            hasMonospaceFont
            onChange={(time) => {
              onChange(time as string);
            }}
            data={inputSelectTimesData}
            value={value}
            disabled={!!disabled}
            style={{ marginLeft: '10px', minWidth: '100px' }}
            dropdownStyle={{ zIndex: 1100 }}
            optionStyle={{ fontFamily: 'Courier New, monospace' }}
            filterOption={filterOption}
          />
        </div>
      </Tooltip>
    </Row>
  );
};
